import {
  PracticeSetupStepCategory,
  PracticeSetupStepId,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  Firestore,
  undeletedFilter,
} from '@principle-theorem/shared';
import { from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Brand } from '../../brand';
import { FeeSchedule } from '../../clinical-charting/fees/fee-schedule/fee-schedule';
import { Practice } from '../practice';
import { PracticeSetupStepValidation } from './practice-setup-step-validation';
import {
  IPageVisitTrackingConfig,
  IPracticeSetupStepDefinition,
  IValidationResult,
} from './practice-setup-step.types';
import { initRawSchema } from '@principle-theorem/editor';

const pageVisitTracking: IPageVisitTrackingConfig = {
  routes: ['settings/workspace/fee-schedules'],
  validationMode: 'all',
};

export const feeScheduleStep: IPracticeSetupStepDefinition = {
  id: PracticeSetupStepId.FeeSchedule,
  category: PracticeSetupStepCategory.FeesAndAccounting,
  title: 'Create and Approve Fee Schedules',
  description: initRawSchema(
    `Principle allows fee schedules to be set up at multiple "levels". For most practices the Workspace level should suffice. If setting up multiple practices with different pricing, the Practice level is useful, and if individual practitioners need their own pricing, Personal Schedules can be made.`
  ),
  dependsOn: [],
  getRoute: (_brand, _practice) => `settings/workspace/fee-schedules`,
  validate$: (practice$, step, options) => {
    return practice$.pipe(
      switchMap((practice) => {
        if (options?.reviewed || options?.skipped) {
          return from([{ valid: true } as IValidationResult]);
        }

        const brandRef = Practice.brandRef(practice);
        const organisationRef = Brand.organisationRef(asReffable(brandRef));

        return from(
          Firestore.getDocs(FeeSchedule.col(asReffable(organisationRef)))
        ).pipe(
          map((rawFeeSchedules) => {
            const feeSchedules = rawFeeSchedules.filter(undeletedFilter);

            if (feeSchedules.length === 0) {
              return { valid: true };
            }

            const hasServiceCodePricing = feeSchedules.some(
              (schedule) =>
                schedule.serviceCodes.length &&
                schedule.serviceCodes.some(
                  (code) =>
                    !!code.pricingRule.ruleItems.some((item) => !!item.price)
                )
            );

            const hasTreatmentPricing = feeSchedules.some(
              (schedule) =>
                schedule.treatmentFees &&
                schedule.treatmentFees.length &&
                schedule.treatmentFees.some((fee) => !!fee.price)
            );

            const hasPricing = hasTreatmentPricing || hasServiceCodePricing;

            if (!hasPricing) {
              return {
                valid: false,
                reason:
                  'At least one fee schedule must have pricing set before completing this step',
              };
            }

            const pageVisitValidation =
              PracticeSetupStepValidation.validatePageVisits(
                step,
                pageVisitTracking,
                feeSchedules.length
              );

            if (pageVisitValidation.valid) {
              return { valid: true };
            }

            const pageVisits = step?.pageVisits ?? [];
            const visitedFeeScheduleIds = new Set(
              pageVisits
                .filter((visit) => visit.route.includes('fee-schedules/'))
                .map((visit) => {
                  const segments = visit.route.split('/');
                  const index = segments.lastIndexOf('fee-schedules');
                  return segments[index + 1];
                })
                .filter((id): id is string => !!id)
            );
            const remainingFeeSchedules = feeSchedules
              .filter((schedule) => !visitedFeeScheduleIds.has(schedule.ref.id))
              .map((schedule) => schedule.name);

            return {
              valid: false,
              reason: `Please review and approve all fee schedules before completing this step (${
                pageVisitValidation.reason
              }). Remaining fee schedules: \r\n${remainingFeeSchedules
                .map((name) => `- ${name}`)
                .sort()
                .join('\r\n')}`,
              progress: {
                current: pageVisitValidation.progress?.current || 0,
                total: feeSchedules.length,
              },
            };
          })
        );
      })
    );
  },
  pageVisitTracking,
};
